// Vite inlines VITE_API_URL for the web apps. The Expo app has no
// import.meta.env (Metro leaves `env` undefined, hence the `?.`) and sets its
// URL with setApiBaseUrl() at startup instead.
let baseUrl: string = import.meta.env?.VITE_API_URL || '/api'

/** Where API requests go. Read at call time, so setApiBaseUrl() applies everywhere. */
export function apiBaseUrl(): string {
  return baseUrl
}

/** Point every API call at `url` (no trailing slash). For runtimes without Vite env. */
export function setApiBaseUrl(url: string) {
  baseUrl = url.replace(/\/$/, '')
}

let accessToken: string | null = null
let refreshPromise: Promise<boolean> | null = null
let refreshFn: (() => Promise<boolean>) | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

/** Call once at app startup to wire up the refresh function (breaks circular dep with store). */
export function configureClient(opts: { refresh: () => Promise<boolean> }) {
  refreshFn = opts.refresh
}

/**
 * A failed API response. Extends Error so existing `err.message` handling is
 * unchanged, and adds the status and parsed body for callers that need to act
 * on a specific failure rather than just show its message — a 409 carrying the
 * reason a request was refused, for instance.
 */
export class ApiError extends Error {
  readonly status: number
  readonly body: Record<string, unknown>

  constructor(message: string, status: number, body: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>
  }
}

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const isFormData = options?.body instanceof FormData
  const headers: Record<string, string> = {
    // Let the browser set Content-Type (with boundary) for FormData
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options?.headers as Record<string, string>),
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  let res = await fetch(`${apiBaseUrl()}${path}`, { ...options, headers })

  // On 401, try refresh exactly once
  if (res.status === 401 && accessToken) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`
      res = await fetch(`${apiBaseUrl()}${path}`, { ...options, headers })
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new ApiError(problemMessage(err, res.status), res.status, err)
  }

  return res.json() as Promise<T>
}

/** One entry of huma's RFC 7807 `errors` array. */
interface ProblemDetail {
  message?: string
  location?: string
  value?: unknown
}

/**
 * Turns a field location like `body.playoffs_best_of` into something readable.
 *
 * The `body.`/`query.` prefix is plumbing the admin didn't type, and the field
 * name is snake_case straight off the wire.
 */
function fieldLabel(location: string): string {
  const path = location.replace(/^(body|query|path|header|cookie)\./, '')
  if (!path) return ''
  return path.replace(/[._]/g, ' ').replace(/\[(\d+)\]/g, ' $1').trim()
}

/**
 * Each entry of a Problem's `errors` array as a readable line — for screens
 * that list them rather than squeeze them into one toast.
 *
 * A `members.{username}` location is a person, not a field: a team refused
 * because some players aren't eligible gets one entry per player, and their
 * username (in `value`) must survive intact rather than go through the
 * snake_case-to-words treatment field names get.
 */
export function problemItems(body: Record<string, unknown>): string[] {
  const details = Array.isArray(body.errors) ? (body.errors as ProblemDetail[]) : []
  return details
    .map(d => {
      const person = d.location?.startsWith('members.') && typeof d.value === 'string' ? d.value : ''
      const label = person || (d.location ? fieldLabel(d.location) : '')
      const text = d.message ?? ''
      if (!label) return text
      return text ? `${label}: ${text}` : label
    })
    .filter(Boolean)
}

/**
 * The sentence to show for a failed request.
 *
 * huma answers a rejected body with Problem Details whose `detail` is the
 * generic "validation failed" and whose `errors` array holds the only part
 * anyone can act on — which field, and why. Reading `detail` alone turned every
 * bad field in every form into an untraceable "validation failed" toast.
 */
export function problemMessage(body: Record<string, unknown>, status: number): string {
  // Legacy `{ error }` shape too, for endpoints not yet migrated to huma.
  const summary = (body.detail ?? body.title ?? body.error) as string | undefined

  const named = problemItems(body)

  if (named.length === 0) return summary ?? `Request failed: ${status}`

  // A toast is a couple of lines. Naming every one of a dozen fields turns it
  // into a wall nobody reads — the first few plus a count is what gets acted on,
  // and ApiError.body still carries the rest for anyone who needs them.
  const shown = named.slice(0, 3).join('; ')
  const rest = named.length > 3 ? ` (and ${named.length - 3} more)` : ''

  // "validation failed" adds nothing once the fields are named, but a summary
  // the API wrote itself does.
  const prefix = summary && !/^validation failed$/i.test(summary) ? `${summary} — ` : ''
  return `${prefix}${shown}${rest}`
}

async function tryRefresh(): Promise<boolean> {
  // Deduplicate concurrent refresh attempts
  if (refreshPromise) return refreshPromise
  refreshPromise = doRefresh()
  const result = await refreshPromise
  refreshPromise = null
  return result
}

async function doRefresh(): Promise<boolean> {
  if (!refreshFn) return false
  return refreshFn()
}
